import { useCallback, useContext, useMemo } from 'react'

import { Progress, Tooltip } from 'antd'
import { isArray } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGraduationCap, faClinicMedical, faMessageSmile, faSchool, faUserGraduate, faCheckCircle } from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'

import StudentQuickMenu from '../../../Components/shared/InternshipsManager/StudentQuickMenu'
import PresetsTooltip from './ShiftTableParts/Tooltips/PresetsTooltip'
import { Contexts, InternshipStates, InternshipStatesCanBeValidated } from '../../../utils/constants'
import InternshipValidationButton from '../../../Components/shared/ShiftsManager/InternshipValidationButton'
import { isStudentEvaluationAvailable, SCHEDULE_VALIDATED } from '../../../utils/entities/internship'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { isObject } from '../../../utils'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import LeftFloatingCheckbox from '../../antd/Checkbox/LeftFloatinCheckbox'
import { SelectContext } from '../../../Providers/SelectProvider'
import CircularButton from '../../antd/Buttons/CircularButton'
import { ColorsPalette } from '../../../config/colors'
import { InstitutionFeedbackDrawerContext } from '../../../Providers/Drawer/InstitutionFeedbackDrawer'
import { InstitutionContext } from '../../../Providers/InstitutionProvider'
import { isInstitutionGroupAdmin } from '../../../utils/roles'
import { getTimezoneLessMoment, numberToTimeString } from '../../../utils/momentjs'
import { EventCodeTypes } from '../../../utils/entities/eventCodeType'

import '../../../assets/shifts-manager.scss'
import SchoolNameWithBadge from '../Badges/SchoolNameWithBadge'

const NO_SECTION_NAME = 'NONE'

const formatMinutes = minutes => {
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60

  return remainder ? `${hours}h${numberToTimeString(remainder)}` : `${hours}h`
}

const InternshipCard = ({
  canValidateDesiderata, canValidateInternship, context, displayInfoButton, internship, inWidgetMode, shifts = [], useCheckbox,
  onClickedDraft, onDeleteSchedule, onSelectInternship, onSelectStudent, onSelectValidation,
  onValidateDesideratas
}) => {
  const { isDraft, isReadOnly, user } = useContext(GlobalContext)
  const { selectedMap, setSelected } = useContext(SelectContext)
  const ic = useContext(InstitutionContext)
  const ifd = useContext(InstitutionFeedbackDrawerContext)

  const t = useSelector(selectTranslate)
  const shiftsMinutes = useMemo(() => {
    let duration = 0

    if (internship && internship.duration && isArray(shifts)) {
      duration = shifts.reduce((total, shift) => {
        if (shift.eventCodeType?.category === EventCodeTypes.ABSENCE) {
          return total
        }

        const startDate = getTimezoneLessMoment(shift.startDate)
        const endDate = getTimezoneLessMoment(shift.endDate)

        if (shift.startTime) {
          const timeParts = shift.startTime.split(':')
          startDate.set({
            hour: timeParts[0],
            minute: timeParts[1]
          })
        }

        if (shift.endTime) {
          const timeParts = shift.endTime.split(':')
          endDate.set({
            hour: timeParts[0],
            minute: timeParts[1]
          })
        }

        const shiftEndDate = endDate.isBefore(startDate) ? endDate.clone().add(1, 'day') : endDate
        const shiftMinutes = shiftEndDate.diff(startDate, 'minutes')

        return total + Math.max(shiftMinutes - Math.max(Math.round(Number(shift.pause)) || 0, 0), 0)
      }, 0)
    }

    return duration
  }, [internship?.duration, shifts])

  const getValidateButtonTooltip = internship => {
    if (internship.state === InternshipStates.SCHEDULE_TO_VALIDATE) {
      return t('internship_infos.validate_button.to_validate')
    }

    if (internship.state === InternshipStates.SCHEDULE_VALIDATED) {
      return t('internship_infos.validate_button.validated')
    }

    return t(`internship_infos.validate_button.${isReadOnly ? 'see' : 'validate'}`)
  }

  const getSectionName = internship => {
    let sectionName = 'NONE'

    if (internship.school === null) {
      sectionName = NO_SECTION_NAME
    } else if (internship.internshipYear?.sectionName !== 'NONE') {
      sectionName = internship.internshipYear?.sectionAbbreviation
    } else if (internship.schoolSection?.comment) {
      sectionName = internship.schoolSection?.comment
    } else if (internship.schoolSection?.name) {
      sectionName = internship.schoolSection?.name
    }

    if (internship.internshipYear) {
      sectionName += ` (${internship.internshipYear.schoolYearName})`
    } else {
      sectionName = t('internship_card.without_study_year')
    }

    return sectionName
  }

  const getStudentName = internship => {
    let name = t('internship_card.without_student')

    if (internship.student) {
      name = `${internship.student.firstname} ${internship.student.lastname}`
    }

    return name
  }

  const remainingMinutes = useMemo(
    () => internship?.duration ? internship.duration * 60 - shiftsMinutes : 0,
    [internship?.duration, shiftsMinutes]
  )

  const durationColor = useMemo(() => {
    if (remainingMinutes > 0) {
      return ColorsPalette.ORANGE
    }

    if (remainingMinutes < 0) {
      return ColorsPalette.BLUE_ICON
    }

    return ColorsPalette.GREEN
  }, [remainingMinutes])

  const formatPercent = useCallback(() => {
    if (remainingMinutes === 0) {
      return <FontAwesomeIcon icon={faCheckCircle} color={ColorsPalette.GREEN} />
    }

    return (
      <span style={{ color: durationColor }}>
        {remainingMinutes > 0 ? formatMinutes(remainingMinutes) : `+${formatMinutes(-remainingMinutes)}`}
      </span>
    )
  }, [durationColor, remainingMinutes])

  return (
    <div className='internship-card-container'>
      <div className='flex-row w-100 jc-between ai-ctr'>
        <div className='internship-card-title'>
          {useCheckbox && internship.state !== SCHEDULE_VALIDATED && !isReadOnly && !inWidgetMode && (
            <LeftFloatingCheckbox
              checked={!!selectedMap[internship.id]}
              style={{ borderSpacing: '0px' }}
              onChange={() => setSelected(prev => selectedMap[internship.id] ? prev.filter(i => i.id !== internship.id) : [...prev, internship])}
            />
          )}
          {!inWidgetMode && (
            <StudentQuickMenu
              canValidateDesiderata={canValidateDesiderata}
              internship={internship}
              onClickedDraft={onClickedDraft}
              onDeleteSchedule={onDeleteSchedule}
              onSelectedStudentInfo={onSelectStudent}
              onSelectedInternshipInfo={onSelectInternship}
              onValidateDesideratas={onValidateDesideratas}
              inShiftsManager
            />
          )}
          <div className='internship-information'>
            <div>
              <div className='regular-image'> <FontAwesomeIcon icon={faUserGraduate} /> </div>
              <span><b> {getStudentName(internship)} - {getSectionName(internship)}</b></span>
            </div>
            <div>
              <div className='regular-image'> <FontAwesomeIcon icon={faSchool} /> </div>
              {context === Contexts.SCHOOL && internship.institution && (
                <span> {internship.institution.name} </span>
              )}
              {context === Contexts.INSTITUTION && internship.school && (
                <span>
                  <SchoolNameWithBadge school={internship.school} badgePosition='left' renderTooltip />
                </span>
              )}
            </div>
            <div>
              <div className='regular-image'> <FontAwesomeIcon icon={faGraduationCap} /> </div>
              <div className='last-text'>
                {internship.sector && internship.sector.parent === null ? t('Without care unit') : internship.sector.name}
              </div>
            </div>
            {isInstitutionGroupAdmin(user) && internship.institution && (
              <div style={{ marginTop: '4px' }}>
                <div className='regular-image'> <FontAwesomeIcon icon={faClinicMedical} /> </div>
                <div className='last-text'>
                  {internship.institution.name}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={'internship-card-buttons' + (useCheckbox ? ' in-draft-mode' : '')}>
          {(displayInfoButton && !inWidgetMode) && (
            <PresetsTooltip
              presets={internship.presets}
              sectorNote={isObject(internship.sector) && isObject(internship.sector.sectorNote) ? internship.sector.sectorNote.note : null}
              internship={internship}
            />
          )}
          {canValidateInternship && !isDraft && InternshipStatesCanBeValidated.includes(internship.state) && internship.student && (
            <InternshipValidationButton
              validateButtonTooltip={getValidateButtonTooltip(internship)}
              internship={internship}
              onDisplayValidationDrawer={onSelectValidation}
            />
          )}
          {isObject(ifd) && isObject(ic) && ic.useRecruitmentModule && !isDraft && internship.state >= InternshipStates.SCHEDULE_ADDED && internship.student && isStudentEvaluationAvailable(internship) && (
            <Tooltip title={t('internship_card.feedback')} placement='right'>
              <div>
                <CircularButton
                  className='mt-8i'
                  color={ColorsPalette.DEEP_SKY_BLUE}
                  icon={faMessageSmile}
                  onClick={() => ifd.setHandler({ data: internship })}
                />
              </div>
            </Tooltip>
          )}
        </div>
      </div>
      {!inWidgetMode && !!internship.duration && (
        <div className='w-100 flex-col'>
          <Tooltip title={`${formatMinutes(shiftsMinutes)} / ${internship.duration}h`} placement='top'>
            <Progress
              percent={(shiftsMinutes / (internship.duration * 60)) * 100}
              strokeColor={durationColor}
              format={formatPercent}
            />
          </Tooltip>
        </div>
      )}
    </div>
  )
}

export default InternshipCard
